// ABOUTME: Dismissible upgrade banner shown to free-tier users after onboarding.
// ABOUTME: Used on investigations, reports, and home pages to nudge toward Pro.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useApp } from '@/context/AppContext';

interface UpgradeBannerProps {
  pageKey: string;
  message: string;
  upgradeLabel?: string;
}

const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function getDismissKey(pageKey: string): string {
  return `upgrade-banner-dismissed:${pageKey}`;
}

function isDismissed(pageKey: string): boolean {
  try {
    const raw = localStorage.getItem(getDismissKey(pageKey));
    if (!raw) return false;
    const data = JSON.parse(raw);
    if (!data.dismissed || !data.timestamp) return false;
    // Re-appear after 7 days
    return Date.now() - data.timestamp < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

export function UpgradeBanner({ pageKey, message, upgradeLabel = 'Upgrade' }: UpgradeBannerProps) {
  const navigate = useNavigate();
  const { isFreeTier, tourCompleted } = useApp();
  const [dismissed, setDismissed] = useState(() => isDismissed(pageKey));

  // Gate: only show for freemium users who completed onboarding
  if (!isFreeTier || !tourCompleted || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(
        getDismissKey(pageKey),
        JSON.stringify({ dismissed: true, timestamp: Date.now() })
      );
    } catch {
      // localStorage not available — dismiss in memory only
    }
    setDismissed(true);
  };

  return (
    <div className="flex items-center justify-between px-4 py-2 bg-primary-50 border border-primary-100 rounded-sm mb-4">
      <div className="flex items-center gap-2 text-sm text-primary-700">
        <span className="text-xs font-medium px-1.5 py-0.5 rounded-sm bg-primary-100 text-primary-700">
          Pro
        </span>
        <span>{message}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/upgrade')}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          {upgradeLabel}
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss upgrade banner"
          className="text-primary-400 hover:text-primary-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
